import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import * as constants from "@learningblocks/storybook-components/lib/PhotosynthesisComponents/MicroPipette/MicroPipette.constants";
import React, { useEffect, useMemo } from "react";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";

type DroppedOn = {
  type:
    | typeof constants.GLASS_TUBE
    | typeof constants.TUBE
    | typeof constants.TRASH
    | typeof constants.MICROPIPETTE_TIP_BOX;
  id: string;
};

export interface SimulationRelatedState {
  droppedOn?: DroppedOn;
  solutionInPipette?: string;
  solutionColor?: string;
  type?:
    | typeof constants.P2
    | typeof constants.P20
    | typeof constants.P200
    | typeof constants.P1000;
  mixing?: {
    droppedOn: string;
    times: number;
  };
}

interface PipetteState {
  type:
    | typeof constants.P2
    | typeof constants.P20
    | typeof constants.P200
    | typeof constants.P1000;
  hasTip: boolean;
  volume: number;
  plungerTooltip: string;
  filled: boolean;
}

const initPipetteState = {
  type: constants.P1000,
  hasTip: false,
  volume: 500,
  plungerTooltip: constants.PLUNGER_TOOLTIP_TYPE.STOPPER,
  filled: true,
};

const initialState: Record<string, PipetteState> = {};

const pipetteSlice = createSlice({
  name: "pipetteScene",
  initialState,
  reducers: {
    initPipette: (
      state,
      { payload: { id } }: PayloadAction<{ id: string }>
    ) => {
      state[id] = initPipetteState;
    },
    setVolume: (
      state,
      { payload: { id, volume } }: PayloadAction<{ id: string; volume: number }>
    ) => {
      let min = 0,
        max = Infinity;
      if (id === "pipette2") {
        min = 0.2;
        max = 2;
      } else if (id === "pipette20") {
        min = 2;
        max = 20;
      } else if (id === "pipette200") {
        min = 20;
        max = 200;
      } else if (id === "pipette1000") {
        min = 200;
        max = 1000;
      }
      if (volume >= min && volume <= max) {
        state[id].volume = volume;
      }
    },
    setTip: (state, { payload: { id } }: PayloadAction<{ id: string }>) => {
      state[id].hasTip = true;
    },
    removeTip: (state, { payload: { id } }: PayloadAction<{ id: string }>) => {
      state[id].hasTip = false;
    },
    togglePlungerTooltip: (
      state,
      { payload: { id } }: PayloadAction<{ id: string }>
    ) => {
      state[id].plungerTooltip =
        state[id].plungerTooltip === constants.PLUNGER_TOOLTIP_TYPE.MIXER
          ? constants.PLUNGER_TOOLTIP_TYPE.STOPPER
          : constants.PLUNGER_TOOLTIP_TYPE.MIXER;
    },
    onFirstStopper: (
      state,
      { payload: { id } }: PayloadAction<{ id: string }>
    ) => {
      state[id].plungerTooltip = constants.PLUNGER_TOOLTIP_TYPE.STOPPER;
    },
    onSecondStopper: (
      state,
      {
        payload: { id, droppedOn },
      }: PayloadAction<{ id: string; droppedOn?: DroppedOn }>
    ) => {
      if (
        (state.hasTip && droppedOn?.type === constants.TUBE) ||
        droppedOn?.type === constants.GLASS_TUBE
      ) {
        state[id].filled = !state.filled;
      }
    },
    onMix: (
      state,
      {
        payload: { id, droppedOn, times },
      }: PayloadAction<{ id: string; droppedOn?: DroppedOn; times?: number }>
    ) => {
      state[id].plungerTooltip = constants.PLUNGER_TOOLTIP_TYPE.MIXER;
    },
  },
});

// Create safer selectors that handle potential errors during reducer execution
const selectPipetteState = createSelector(
  [(state: any) => state || {}],
  (state) => {
    try {
      return state?.pipetteScene || {};
    } catch (e) {
      console.warn("Error accessing pipetteScene state", e);
      return {};
    }
  }
);

const selectSimulationState = createSelector(
  [(state: any) => state || {}],
  (state) => {
    try {
      return state?.simulation || {};
    } catch (e) {
      console.warn("Error accessing simulation state", e);
      return {};
    }
  }
);

export const usePipette = (
  id: string
): [
  state: PipetteState & SimulationRelatedState,
  actions: {
    [K in keyof typeof pipetteSlice.actions]: (
      payload: ReturnType<(typeof pipetteSlice.actions)[K]>["payload"]
    ) => void;
  },
] => {
  const dispatch = useDispatch();
  const actions = pipetteSlice.actions;

  // Create memoized selectors for specific items to prevent accessing during reducer execution
  const selectItemState = useMemo(
    () =>
      createSelector(
        [selectPipetteState],
        (pipetteState) => pipetteState[id] || null
      ),
    [id]
  );

  const selectDroppedOn = useMemo(
    () =>
      createSelector([selectSimulationState], (simulation) => {
        if (!simulation[id]) return { type: "" };
        return simulation[id]?.droppedOn || { type: "" };
      }),
    [id]
  );

  const selectItemSimulationState = useMemo(
    () =>
      createSelector([selectSimulationState], (simulation) => {
        if (!simulation[id]) {
          return {
            solutionInPipette: undefined,
            droppedOn: undefined,
            solutionColor: undefined,
            type: constants.P1000,
            times: undefined,
          };
        }
        return {
          solutionInPipette: simulation[id].solutionInPipette,
          droppedOn: simulation[id].droppedOn,
          solutionColor: simulation[id].solutionColor,
          type: simulation[id].type || constants.P1000,
          times: simulation[id].mixing?.times,
        };
      }),
    [id]
  );

  // Use our safer memoized selectors - these won't trigger the "store.getState() during reducer" error
  const itemState = useSelector(selectItemState);
  const droppedPipetteOn = useSelector(selectDroppedOn);
  const simulationState = useSelector(selectItemSimulationState);

  useEffect(() => {
    if (!itemState) {
      dispatch(actions.initPipette({ id }));
    }
  }, [id, itemState, dispatch]);

  useEffect(() => {
    if (droppedPipetteOn && droppedPipetteOn.type) {
      const { type } = droppedPipetteOn;
      switch (type) {
        case constants.MICROPIPETTE_TIP_BOX:
          dispatch(actions.setTip({ id }));
          break;
        default:
      }
    }
  }, [droppedPipetteOn, id, dispatch]);

  useEffect(() => {
    if (simulationState.type === constants.P1000) {
      dispatch(actions.setVolume({ id, volume: 500 }));
    } else if (simulationState.type === constants.P200) {
      dispatch(actions.setVolume({ id, volume: 100 }));
    } else if (simulationState.type === constants.P20) {
      dispatch(actions.setVolume({ id, volume: 10 }));
    } else if (simulationState.type === constants.P2) {
      dispatch(actions.setVolume({ id, volume: 1 }));
    }
  }, [simulationState.type, id, dispatch]);

  // Create a stable reference for the returned action functions
  const pipetteActions = React.useMemo(() => {
    return Object.entries(actions).reduce(
      (newActions, [key, action]) => {
        const typedAction = action as ActionCreatorWithPayload<any>;
        return {
          ...newActions,
          [key]: (payload: any = {}) => {
            if (key === "onMix") {
              // Get current values from state directly at the time of dispatch
              const currentDroppedOn = simulationState.droppedOn;
              const currentTimes = simulationState.times;

              dispatch(
                typedAction({
                  id,
                  ...payload,
                  droppedOn: currentDroppedOn,
                  times: currentTimes,
                })
              );
            } else {
              dispatch(typedAction({ id, ...payload }));
            }
          },
        };
      },
      {} as {
        [K in keyof typeof pipetteSlice.actions]: (
          payload: Omit<
            ReturnType<(typeof pipetteSlice.actions)[K]>["payload"],
            "id"
          >
        ) => void;
      }
    );
  }, [actions, dispatch, id, simulationState.droppedOn, simulationState.times]);

  return [
    {
      ...(itemState || {}), // Use itemState instead of state[id]
      ...simulationState,
    },
    pipetteActions,
  ];
};

export const {
  actions: {
    onFirstStopper,
    onSecondStopper,
    onMix,
    setVolume,
    setTip,
    removeTip,
    togglePlungerTooltip,
  },
  reducer: PipetteReducer,
  name: PipetteReducerName,
} = pipetteSlice;
